const API_URL = process.env.REACT_APP_API_URL || 'https://api.digitalwerks.com';

export const resolveApiUrl = (value) => {
  if (!value) return '';
  try { return new URL(value, API_URL).toString(); }
  catch (_) { return ''; }
};

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

const request = async (path, options = {}) => {
  const headers = new Headers(options.headers || {});
  if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options, headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(payload.error?.message || payload.message || payload.msg || 'The request could not be completed.', response.status, payload);
  }
  return payload;
};

const body = (value) => JSON.stringify(value);
const adminRoot = '/identity/admin/v1';
const queryString = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    params.set(key, Array.isArray(value) ? value.join(',') : value);
  });
  return params.toString();
};

export const adminApi = {
  lightboxHandoff: (values) => request('/identity/lightbox-handoff', { method: 'POST', body: body(values) }),
  signIn: (credentials) => request('/identity/session/password', { method: 'POST', body: body(credentials) }),
  exchange: (tokens) => request('/identity/session/exchange', { method: 'POST', body: body(tokens) }),
  session: () => request('/identity/session'),
  signOut: () => request('/identity/session', { method: 'DELETE' }).catch(() => undefined),
  listUsers: (query) => request(`${adminRoot}/users?${queryString(query)}`),
  getUser: (userId) => request(`${adminRoot}/users/${encodeURIComponent(userId)}`),
  createUser: (values) => request(`${adminRoot}/users`, { method: 'POST', body: body(values) }),
  updateProfile: (userId, values) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/profile`, { method: 'PATCH', body: body(values) }),
  updateSecurity: (userId, values) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/security`, { method: 'PATCH', body: body(values) }),
  deleteUser: (userId) => request(`${adminRoot}/users/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  getCatalog: () => request(`${adminRoot}/catalogs`),
  updateRoles: (userId, values) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/roles`, { method: 'PUT', body: body(values) }),
  updateApplications: (userId, values) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/applications`, { method: 'PUT', body: body(values) }),
  uploadAvatar: (userId, file) => {
    const data = new FormData();
    data.append('avatar', file);
    return request(`${adminRoot}/users/${encodeURIComponent(userId)}/avatar`, { method: 'PUT', body: data });
  },
  removeAvatar: (userId) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/avatar`, { method: 'DELETE' }),
  sendInvitation: (userId, applicationId) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/invitations`, { method: 'POST', body: body({ applicationId }) }),
  sendPasswordReset: (userId, applicationId) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/password-reset`, { method: 'POST', body: body({ applicationId }) }),
  getAudit: (userId, query = {}) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/activity?${new URLSearchParams(query).toString()}`),
  getLoginHistory: (userId, query = {}) => request(`${adminRoot}/users/${encodeURIComponent(userId)}/login-history?${new URLSearchParams(query).toString()}`),
};

export const normalizeList = (payload) => ({
  items: payload.items || payload.users || payload.data || [],
  page: payload.page || payload.pagination?.page || 1,
  pageSize: payload.pageSize || payload.pagination?.pageSize || payload.pagination?.limit || 20,
  total: payload.total ?? payload.pagination?.total ?? 0,
});
